import {memo, type ReactElement, useCallback} from 'react';
import isEqual from 'react-fast-compare';

import classNames from 'classnames';

import type {LinkApiModel} from '@yourcoach/shared/api/media/link';

import FolderPrimaryIcon from '@yourcoach/shared/assets/icons-colored/folder-primary.svg';
import FolderSecondaryIcon from '@yourcoach/shared/assets/icons-colored/folder-secondary.svg';
import MoreIcon from '@yourcoach/shared/assets/icons/more.svg';
import StarIcon from '@yourcoach/shared/assets/icons/star.svg';

import Button from '@src/components/Button';
import Checkbox from '@src/components/Checkbox';

import styles from './FolderCard.module.css';

type Folder = LinkApiModel;

interface Props<T> {
  className?: string;
  folder: T;
  isPartiallySelected?: boolean;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  isStarred?: boolean;
  isSystem?: boolean;
  onCheckboxClick?: (folder: T) => void;
  onClick?: (folder: T) => void;
  onMoreButtonClick?: (
    folder: T,
    event: React.MouseEvent<Element, MouseEvent>,
  ) => void;
}

function FolderCard<T extends Folder = Folder>(
  props: Props<T>,
): ReactElement<typeof FolderCard> {
  const {
    className,
    folder,
    isSelected,
    isSelectionMode,
    isStarred,
    isSystem,
    onCheckboxClick,
    onClick,
    onMoreButtonClick,
  } = props;

  const onClickCb: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();
      onClick?.(folder);
    },
    [folder, onClick],
  );

  const onMoreButtonClickCb: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      onMoreButtonClick?.(folder, e);
    },
    [folder, onMoreButtonClick],
  );

  const onCheckboxClickCb: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      onCheckboxClick?.(folder);
    },
    [onCheckboxClick, folder],
  );

  return (
    <div
      className={classNames(
        styles.container,
        isSystem && styles.system,
        className,
      )}
    >
      <div className={styles.content} onClick={onClickCb}>
        <div className={styles.iconContainer}>
          {isSystem ? <FolderPrimaryIcon /> : <FolderSecondaryIcon />}
          {isStarred ? <StarIcon className={styles.starIcon} /> : null}
        </div>
        <div className={styles.dataContainer}>
          <p className={styles.name}>{folder.name}</p>
        </div>
      </div>
      {isSelectionMode && !isSystem && !onMoreButtonClick ? (
        <div className={styles.checkboxContainer} onClick={onCheckboxClickCb}>
          <Checkbox
            checked={isSelected}
            // TODO: Add round and dot checkbox varian
            // variant={isPartiallySelected ? 'dot' : 'round'}
          />
        </div>
      ) : null}
      {onMoreButtonClick && !isSelectionMode ? (
        <Button className={styles.moreButton} onClick={onMoreButtonClickCb}>
          <MoreIcon />
        </Button>
      ) : null}
    </div>
  );
}

export default memo(FolderCard, isEqual) as typeof FolderCard;
